import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { useLoginMutation } from '@/api/mutations/auth'
import { useCaptchaQuery } from '@/api/queries/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { usePageTitle } from '@/lib/use-page-title'

const decodeBase64 = (value: string) => {
  const binary = atob(value)
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

const getCaptchaSrc = (captchaBase64: string | undefined) => {
  if (!captchaBase64) return ''
  if (captchaBase64.startsWith('data:')) return captchaBase64

  try {
    const decoded = decodeBase64(captchaBase64).trim()
    if (decoded.startsWith('<svg')) {
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(decoded)}`
    }
  } catch {
    return `data:image/png;base64,${captchaBase64}`
  }

  return `data:image/png;base64,${captchaBase64}`
}

export const Login = () => {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const login = useLoginMutation()
  const captcha = useCaptchaQuery()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [captchaCode, setCaptchaCode] = useState('')

  usePageTitle('login_title', 'auth')

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!captcha.data) {
      toast.error(t('captcha_load_failed'))
      await captcha.refetch()
      return
    }
    if (!username) {
      toast.error(t('validation.username_required'))
      return
    }
    if (!password) {
      toast.error(t('validation.password_required'))
      return
    }
    if (!captchaCode) {
      toast.error(t('validation.captcha_required'))
      return
    }

    try {
      await login.mutateAsync({
        username,
        password,
        captchaId: captcha.data.captchaId,
        captchaCode,
      })
      navigate(searchParams.get('redirect') || '/workbench', { replace: true })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('login_failed'))
      setCaptchaCode('')
      await captcha.refetch()
    }
  }

  const captchaSrc = getCaptchaSrc(captcha.data?.captchaBase64)

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">{t('login_title')}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('login_description')}
        </p>
      </div>
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="space-y-2">
          <Label htmlFor="username">{t('username')}</Label>
          <Input
            id="username"
            value={username}
            autoComplete="username"
            onChange={(event) => setUsername(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">{t('password')}</Label>
          <Input
            id="password"
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="captchaCode">{t('captcha')}</Label>
          <div className="flex gap-2">
            <Input
              id="captchaCode"
              value={captchaCode}
              autoComplete="off"
              inputMode="text"
              onChange={(event) => setCaptchaCode(event.target.value)}
            />
            <button
              type="button"
              className="h-10 w-28 shrink-0 overflow-hidden rounded border border-input bg-card text-xs text-muted-foreground disabled:opacity-50"
              disabled={captcha.isFetching}
              onClick={() => {
                setCaptchaCode('')
                void captcha.refetch()
              }}
              aria-label={t('refresh_captcha')}
            >
              {captcha.data ? (
                <img
                  src={captchaSrc}
                  alt={t('captcha_image_alt')}
                  className="h-full w-full object-cover"
                />
              ) : (
                t(
                  captcha.isFetching
                    ? 'captcha_loading'
                    : 'refresh_captcha',
                )
              )}
            </button>
          </div>
        </div>
        <Button className="w-full" disabled={login.isPending}>
          {login.isPending ? t('logging_in') : t('login_button')}
        </Button>
      </form>
    </div>
  )
}
